import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import {
  FiArrowLeft,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiPackage,
  FiPhone,
  FiRefreshCw,
  FiShoppingBag,
} from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { firestore } from "../../utils/firebase";
import { AdminContext } from "../../layouts/Admin";
import "./Siparisler.css";

const PAGE_SIZE = 40;
const TOTAL_BATCH_SIZE = 200;

const getDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = getDate(value);
  return date ? date.toLocaleDateString("tr-TR") : "—";
};

const formatTime = (value) => {
  const date = getDate(value);
  return date
    ? date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
    : "—";
};

const formatCurrency = (value) => `${(Number(value) || 0).toFixed(2)} TL`;

const getLocalDateInputValue = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

function OrdersTable() {
  const history = useHistory();
  const { tablesObj, menuObj } = useContext(AdminContext);
  const cafe = localStorage.getItem("name");
  const today = getLocalDateInputValue(new Date());

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [orderType, setOrderType] = useState("orders");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeTotal, setRangeTotal] = useState(null);
  const [rangeTotalLoading, setRangeTotalLoading] = useState(true);
  const [rangeTotalError, setRangeTotalError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const orderTypeInfo = {
    orders: { label: "Masa siparişleri", shortLabel: "Masa", icon: FiGrid },
    "paket-orders": { label: "Paket siparişleri", shortLabel: "Paket", icon: FiPackage },
    "phone-orders": { label: "Telefon siparişleri", shortLabel: "Telefon", icon: FiPhone },
  };

  const visibleOrders = useCallback(
    (items) =>
      items.filter((item) => {
        if (orderType === "orders") return item.status == 1 && item.arama == null;
        if (orderType === "paket-orders") return item.status == 2 && item.paket === true;
        return item.status == 2 && item.arama === true;
      }),
    [orderType]
  );

  const calculateTotal = useCallback(
    (order) => {
      if (order.arama || order.paket) return Number(order.total) || 0;

      return (order.cart || []).reduce((total, cartItem) => {
        const itemTotal = Number(cartItem.total) || 0;
        const optionTotal = Number(cartItem.optPrice) || 0;
        const paid = Number(cartItem.paid) || 0;
        return total + (itemTotal + optionTotal) * paid;
      }, 0);
    },
    []
  );

  const createOrdersQuery = useCallback(
    () => {
      const collection = orderType === "orders" ? "orders" : "paket-orders";
      return firestore
        .collection(`Restaurants/${cafe}/${collection}`)
        .where("date", ">=", new Date(`${startDate}T00:00:00`))
        .where("date", "<=", new Date(`${endDate}T23:59:59.999`))
        .orderBy("date", "desc");
    },
    [cafe, endDate, orderType, startDate]
  );

  const fetchPage = useCallback(
    async (cursor) => {
      const query = createOrdersQuery();

      const snapshot = await (cursor
        ? query.startAfter(cursor).limit(PAGE_SIZE + 1).get()
        : query.limit(PAGE_SIZE + 1).get());
      const rawDocs = snapshot.docs;
      const docs = rawDocs.slice(0, PAGE_SIZE);

      return {
        items: visibleOrders(docs.map((doc) => ({ ...doc.data(), id: doc.id }))),
        cursor: docs[docs.length - 1] || null,
        hasNext: rawDocs.length > PAGE_SIZE,
      };
    },
    [createOrdersQuery, visibleOrders]
  );

  const fetchRangeTotal = useCallback(async () => {
    let cursor = null;
    let hasNext = true;
    let total = 0;
    let count = 0;

    while (hasNext) {
      const query = createOrdersQuery();
      const snapshot = await (cursor
        ? query.startAfter(cursor).limit(TOTAL_BATCH_SIZE + 1).get()
        : query.limit(TOTAL_BATCH_SIZE + 1).get());
      const rawDocs = snapshot.docs;
      const docs = rawDocs.slice(0, TOTAL_BATCH_SIZE);
      const orders = visibleOrders(docs.map((doc) => ({ ...doc.data(), id: doc.id })));

      total += orders.reduce((sum, order) => sum + calculateTotal(order), 0);
      count += orders.length;
      hasNext = rawDocs.length > TOTAL_BATCH_SIZE;
      cursor = docs[docs.length - 1] || null;
    }

    return { total, count };
  }, [calculateTotal, createOrdersQuery, visibleOrders]);

  useEffect(() => {
    let active = true;

    if (startDate > endDate) {
      setPages({ 1: { items: [], cursor: null, hasNext: false } });
      setPage(1);
      setLoading(false);
      setRangeTotal(0);
      setRangeTotalLoading(false);
      setRangeTotalError(false);
      setError("Başlangıç tarihi, bitiş tarihinden sonra olamaz.");
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError(null);
    setPages({});
    setPage(1);
    setRangeTotal(null);
    setRangeTotalLoading(true);
    setRangeTotalError(false);

    fetchPage(null)
      .then((result) => {
        if (!active) return;
        setPages({ 1: result });
      })
      .catch((fetchError) => {
        if (!active) return;
        setError(fetchError);
        setPages({ 1: { items: [], cursor: null, hasNext: false } });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    fetchRangeTotal()
      .then((result) => {
        if (!active) return;
        setRangeTotal(result.total);
      })
      .catch(() => {
        if (active) setRangeTotalError(true);
      })
      .finally(() => {
        if (active) setRangeTotalLoading(false);
      });

    return () => {
      active = false;
    };
  }, [endDate, fetchPage, fetchRangeTotal, refreshKey, startDate]);

  const currentPage = pages[page] || { items: [], cursor: null, hasNext: false };
  const items = currentPage.items;
  const activeType = orderTypeInfo[orderType];
  const ActiveIcon = activeType.icon;

  const getOrderItems = (order) =>
    (order.cart || []).map((cartItem, index) => {
      const meal = menuObj?.[cartItem.meal];
      const options = (cartItem.checkedOptions || [])
        .map((option) => option.name)
        .filter(Boolean);

      return (
        <div className="orders-page__item" key={`${order.id}-${cartItem.meal}-${index}`}>
          <span className="orders-page__item-quantity">{cartItem.paid || 1}×</span>
          <span>
            {meal?.name || cartItem.name || "Ürün"}
            {options.length > 0 && (
              <small className="orders-page__item-options">+ {options.join(" · ")}</small>
            )}
          </span>
        </div>
      );
    });

  const getPlace = (order) => {
    if (orderType === "phone-orders") return order.phone || "Telefon siparişi";
    if (orderType === "paket-orders") return order.address || order.name || "Paket siparişi";
    return tablesObj?.[order.table]?.name || "Masa bilgisi yok";
  };

  const changeOrderType = (nextType) => {
    if (nextType === orderType) return;
    setOrderType(nextType);
  };

  const goToNextPage = async () => {
    if (!currentPage.hasNext || loading) return;
    const nextPage = page + 1;

    if (pages[nextPage]) {
      setPage(nextPage);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchPage(currentPage.cursor);
      setPages((previousPages) => ({ ...previousPages, [nextPage]: result }));
      setPage(nextPage);
    } catch (fetchError) {
      setError(fetchError);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousPage = () => {
    if (!loading && page > 1) setPage(page - 1);
  };

  const refresh = () => {
    setLoading(true);
    setRangeTotalLoading(true);
    setError(null);
    setRefreshKey((key) => key + 1);
  };

  return (
    <main className="orders-page">
      <header className="orders-page__header">
        <Button
          variant="light"
          className="orders-page__back"
          onClick={() => history.push("/admin")}
          aria-label="Yönetim paneline dön"
        >
          <FiArrowLeft size={20} />
        </Button>
        <div>
          <div className="orders-page__eyebrow">GEÇMİŞ KAYITLAR</div>
          <h1>Sipariş geçmişi</h1>
          <p>Tarih aralığına göre tamamlanan siparişleri hızlıca inceleyin.</p>
        </div>
        <div className="orders-page__connection">
          <span className="orders-page__connection-dot" />
          <span>{loading ? "Güncelleniyor" : "Arşiv görünümü"}</span>
        </div>
      </header>

      <section className="orders-page__filters" aria-label="Sipariş filtreleri">
        <div className="orders-page__tabs" role="tablist" aria-label="Sipariş türü">
          {Object.entries(orderTypeInfo).map(([type, info]) => {
            const Icon = info.icon;
            const active = type === orderType;
            return (
              <button
                className={`orders-page__tab ${active ? "is-active" : ""}`}
                key={type}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => changeOrderType(type)}
              >
                <Icon size={18} />
                <span>{info.label}</span>
              </button>
            );
          })}
        </div>

        <div className="orders-page__date-filter">
          <div className="orders-page__date-field">
            <label htmlFor="orders-start-date">Başlangıç</label>
            <div className="orders-page__input-wrap">
              <FiCalendar size={17} />
              <Form.Control
                id="orders-start-date"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
          </div>
          <span className="orders-page__date-separator">—</span>
          <div className="orders-page__date-field">
            <label htmlFor="orders-end-date">Bitiş</label>
            <div className="orders-page__input-wrap">
              <FiCalendar size={17} />
              <Form.Control
                id="orders-end-date"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
          </div>
          <button type="button" className="orders-page__refresh" onClick={refresh} aria-label="Listeyi yenile">
            <FiRefreshCw size={17} />
          </button>
        </div>
      </section>

      <section className="orders-page__summary" aria-label="Sipariş özeti">
        <div className="orders-page__summary-card">
          <span>Bu sayfa</span>
          <strong>{items.length}</strong>
          <small>{PAGE_SIZE} kayıt / sayfa</small>
        </div>
        <div className="orders-page__summary-card orders-page__summary-card--accent">
          <span>Tarih aralığı toplamı</span>
          <strong>{rangeTotalLoading ? "Hesaplanıyor..." : rangeTotalError ? "—" : formatCurrency(rangeTotal)}</strong>
          <small>Seçilen tarihlerdeki tüm {activeType.shortLabel.toLocaleLowerCase("tr-TR")} siparişleri</small>
        </div>
        <div className="orders-page__summary-card orders-page__summary-card--quiet">
          <span>Filtre</span>
          <strong><ActiveIcon size={20} /> {activeType.shortLabel}</strong>
          <small>{formatDate(startDate)} — {formatDate(endDate)}</small>
        </div>
      </section>

      <section className="orders-page__table-card">
        <div className="orders-page__table-heading">
          <div>
            <h2>Siparişler</h2>
            <p>{loading && items.length === 0 ? "Kayıtlar yükleniyor..." : `${page}. sayfa · En yeni siparişler üstte`}</p>
          </div>
          <div className="orders-page__table-count">{items.length} kayıt</div>
        </div>

        {error && (
          <div className="orders-page__message orders-page__message--error">
            Siparişler yüklenirken bir hata oluştu: {error.message || error}
          </div>
        )}

        <div className={`orders-page__table-wrap ${loading ? "is-loading" : ""}`}>
          <Table className="orders-page__table" responsive>
            <thead>
              <tr>
                <th>Zaman</th>
                <th>Sipariş içeriği</th>
                <th>{orderType === "phone-orders" ? "Telefon" : orderType === "paket-orders" ? "Teslimat" : "Masa"}</th>
                <th className="text-end">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const orderItems = getOrderItems(item);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="orders-page__date-cell">
                        <strong>{formatDate(item.date)}</strong>
                        <span>{formatTime(item.date)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="orders-page__items-cell">
                        {orderItems.length > 0 ? orderItems : <span>İçerik bulunamadı</span>}
                      </div>
                    </td>
                    <td>
                      <span className="orders-page__place">
                        {orderType === "phone-orders" ? <FiPhone size={15} /> : orderType === "paket-orders" ? <FiPackage size={15} /> : <FiGrid size={15} />}
                        {getPlace(item)}
                      </span>
                    </td>
                    <td className="text-end">
                      <strong className="orders-page__amount">{formatCurrency(calculateTotal(item))}</strong>
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="4">
                    <div className="orders-page__empty">
                      <FiShoppingBag size={28} />
                      <strong>Bu filtrelerde sipariş bulunamadı</strong>
                      <span>Tarih aralığını veya sipariş türünü değiştirmeyi deneyin.</span>
                    </div>
                  </td>
                </tr>
              )}
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan="4">
                    <div className="orders-page__loading-row">
                      <span className="orders-page__spinner" /> Siparişler hazırlanıyor...
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="3">Seçilen tarih aralığı toplamı</td>
                  <td className="text-end">
                    {rangeTotalLoading ? "Hesaplanıyor..." : rangeTotalError ? "—" : formatCurrency(rangeTotal)}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>

        <div className="orders-page__pagination">
          <span>{page > 1 ? `${page}. sayfa` : "İlk sayfa"}</span>
          <div>
            <button type="button" onClick={goToPreviousPage} disabled={page === 1 || loading}>
              <FiChevronLeft size={17} /> Önceki
            </button>
            <button type="button" onClick={goToNextPage} disabled={!currentPage.hasNext || loading}>
              Sonraki <FiChevronRight size={17} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default OrdersTable;
